import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AutomapperService } from "../core/automapper/automapper.service";
import { BASE_API_URL } from "../core/environment.tokens";
// import { GainSightPxService } from "../core/meta/gainsightPx.service";
import { LocalService } from "../core/storage/local.service";
import { SessionService } from "../core/storage/session.service";
import { StringHelper } from "../utilities/contracts/string-helper";
import { UserToken } from "./user-token.model";

// TODO: Put in a service before using somewhere else.
// TODO: This doesn't work? Testing for now...
if (environment.production) {
  (window as any).newrelic = (window as any).newrelic || {};
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  readonly AUTHENTICATION_KEY = "authentication";
  readonly tempAuthKey = "temp_auth";
  redirectUrl = "";

  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private router: Router,
    private automapper: AutomapperService,
    private readonly http: HttpClient,
    private readonly session: SessionService,
    private readonly local: LocalService
    // private readonly gainSightPxService: GainSightPxService
  ) { }

  get user(): UserToken {
    const user = this.session.get(this.AUTHENTICATION_KEY, {});
    return new UserToken(user);
  }

  get isAuthenticated(): boolean {
    return this.user.isAuthenticated;
  }

  get username(): string {
    return this.user.username;
  }

  get userId(): number {
    return this.user.userId;
  }

  login(username: string, password: string): void {
    this.sendLoginRequest(username, password).subscribe(
      this.loginSuccess
    );
  }

  logout(): void {
    this.sendLogoutRequest(this.username).subscribe();
    this.logoutComplete();
  }

  private sendLoginRequest(username: string, password: string): Observable<UserToken> {
    const url = `${this.baseApiUrl}authentication/login`;

    return this.http
      .post(url, { userName: username, password })
      .pipe(
        map(this.automapper.curry("default", "UserToken"))
      );
  }

  private loginSuccess = (user: UserToken): void => {
    if (user.isMFAEnabled) {
      this.setTemporaryTokenForMfa(user);
      this.router.navigateByUrl("login/mfa");
    } else {
      this.loginVerified(user);
    }
  }

  private setUserToken(value: UserToken): void {
    this.session.put(this.AUTHENTICATION_KEY, value);
  }

  private setTemporaryTokenForMfa(value: UserToken): void {
    this.session.put(this.tempAuthKey, value);
  }

  get tempUserTokenForMfa(): UserToken {
    const user = this.session.get(this.tempAuthKey, {});
    return new UserToken(user);
  }

  private removeTempUserTokenForMfa() {
    this.session.delete(this.tempAuthKey);
  }

  sendLogoutRequest(username: string): Observable<object> {
    const url = `${this.baseApiUrl}authentication/logout`;
    return this.http.post(url, { userName: username });
  }

  private logoutComplete = (): void => {
    this.session.clear();
    this.local.clear();

    // NOTE: When users logout we refresh to possibly get newly deployed code.
    this.local.put("refresh", true);

    this.router.navigate(["login"]);
  }

  getMicroserviceUrl(): Observable<string> {
    const url = `${this.baseApiUrl}authentication/identitymicroserviceurl`;
    return this.http.get(url).pipe(map((response: any) => response as string));
  }

  loginVerified(user: UserToken) {
    this.setUserToken(user);
    // this.redirectUrl = StringHelper.isAvailable(user.userOnboardingUrl) ? user.userOnboardingUrl : user.homePageUrl;
    this.redirectUrl = user.homePageUrl;

    // TODO: Put in a service before using somewhere else.
    // TODO: This doesn't work? Testing for now...
    if (environment.production) {
      (window as any).newrelic.setCustomAttribute("mrcsUserId", user.userId);
      (window as any).newrelic.setCustomAttribute("mrcsOrgId", user.organizationId);
    }
    // this.gainSightPxService.initialize(user);


    // if (user.isTemporaryPassword) {
    //   this.redirectUrl = "/onboarding";
    // }
    this.router.navigateByUrl(this.redirectUrl);
  }

  get userOrganizationName(): string {
    return this.user.organizationName;
  }

  get userFirstName(): string {
    return this.user.firstName;
  }

  get userLastName(): string {
    return this.user.lastName;
  }
}
